import React from 'react';

import { lazyRetryGeneration } from '../utils/lazyRetry';

// CORE-638 — route-level code splitting for the web bundle.
//
// App.tsx used to pull every screen in through one static import from
// `./src/screens`, so the first chunk carried the practice uploader, the
// calendar, the legal documents and the live room whether or not anyone
// opened them — and, transitively, whatever those screens import (livekit,
// the heavy editors). Each screen below is its own async chunk instead;
// Metro emits one file per `import()` and the navigator pulls it on first
// visit, behind the Suspense boundary in App.tsx.
//
// Home and Login stay eager in App.tsx: they ARE the first screen, so
// deferring them would only add a round-trip.
//
// The `.then` shims exist because most screens are named exports while
// React.lazy wants a module with a default.

type ScreenModule = { default: React.ComponentType<any> };

/**
 * CORE-1299 — `React.lazy`, которому можно скачать чанк заранее.
 *
 * `navigation.reset` на экран, который при первом рендере уходит в Suspense,
 * оставляет адрес старым: пока экран висит, навигатор отдаёт React Navigation
 * прежнее состояние, адрес пишется по нему, а когда экран отрисовался, нового
 * события уже нет. Возврат после входа так и застревал на `/login?returnTo=…`
 * (QA 29.09).
 *
 * Скачать чанк мало: голый `React.lazy` уходит в Suspense на первом рендере
 * даже с модулем в кэше — его собственный промис ещё не разрешён. Поэтому
 * скачанный через `preload` экран рендерится напрямую, без `React.lazy`.
 */
export function lazyScreen(load: () => Promise<ScreenModule>) {
  let loaded: React.ComponentType<any> | null = null;
  // CORE-1517 — `React.lazy` запоминает отказ навсегда: после первой неудачи
  // (чанк не скачался — сеть, stale-хеш) он на каждом рендере бросает ту же
  // сохранённую ошибку и больше за модулем не ходит. «Попробовать снова» на
  // экране ошибки от этого не меняла ничего даже с вернувшейся сетью. Поэтому
  // на повторе (bumpLazyRetry из ErrorBoundary) упавший ленивый компонент
  // подменяется свежим, и следующий рендер запускает новый import(). Подменять
  // сразу при отказе нельзя — см. utils/lazyRetry.ts.
  let failed = false;
  let seenGeneration = lazyRetryGeneration();
  const attempt = (): Promise<ScreenModule> => load().then(
    (m) => { loaded = m.default; return m; },
    (err: unknown) => { failed = true; throw err; },
  );
  let Lazy = React.lazy(attempt);
  const Screen = (props: any) => {
    if (failed && lazyRetryGeneration() !== seenGeneration) {
      failed = false;
      seenGeneration = lazyRetryGeneration();
      Lazy = React.lazy(attempt);
    }
    return React.createElement(loaded ?? Lazy, props);
  };
  return Object.assign(Screen, { preload: attempt });
}


export const AboutPlatformScreen = lazyScreen(() =>
  import('./AboutPlatformScreen').then((m) => ({ default: m.AboutPlatformScreen })));

export const AboutScreen = lazyScreen(() =>
  import('./AboutScreen').then((m) => ({ default: m.AboutScreen })));

export const AgapeCenterScreen = lazyScreen(() =>
  import('./AgapeCenterScreen').then((m) => ({ default: m.AgapeCenterScreen })));

export const AgapePracticeScreen = lazyScreen(() =>
  import('./AgapePracticeScreen').then((m) => ({ default: m.AgapePracticeScreen })));

export const BlockedUsersScreen = lazyScreen(() =>
  import('./BlockedUsersScreen').then((m) => ({ default: m.BlockedUsersScreen })));

export const ChatThreadScreen = lazyScreen(() =>
  import('./ChatThreadScreen').then((m) => ({ default: m.ChatThreadScreen })));

export const ChatsScreen = lazyScreen(() =>
  import('./ChatsScreen').then((m) => ({ default: m.ChatsScreen })));

export const CircleChatScreen = lazyScreen(() =>
  import('./CircleChatScreen').then((m) => ({ default: m.CircleChatScreen })));

export const ConfirmEmailScreen = lazyScreen(() =>
  import('./ConfirmEmailScreen').then((m) => ({ default: m.ConfirmEmailScreen })));

export const DownloadScreen = lazyScreen(() =>
  import('./DownloadScreen').then((m) => ({ default: m.DownloadScreen })));

export const ContactScreen = lazyScreen(() =>
  import('./ContactScreen').then((m) => ({ default: m.ContactScreen })));

export const CreateLiveSessionScreen = lazyScreen(() =>
  import('./live-session/create/CreateLiveSessionScreen').then((m) => ({ default: m.CreateLiveSessionScreen })));

export const CustDevScreen = lazyScreen(() =>
  import('./CustDevScreen').then((m) => ({ default: m.CustDevScreen })));

export const DashboardScreen = lazyScreen(() =>
  import('./DashboardScreen').then((m) => ({ default: m.DashboardScreen })));

export const DocsScreen = lazyScreen(() =>
  import('./DocsScreen').then((m) => ({ default: m.DocsScreen })));

export const EventDetailScreen = lazyScreen(() =>
  import('./EventDetailScreen').then((m) => ({ default: m.EventDetailScreen })));

export const EventParticipantsScreen = lazyScreen(() =>
  import('./EventParticipantsScreen').then((m) => ({ default: m.EventParticipantsScreen })));

export const EventThreadScreen = lazyScreen(() =>
  import('./EventThreadScreen').then((m) => ({ default: m.EventThreadScreen })));

export const EnvironmentScreen = lazyScreen(() =>
  import('./InfoPageScreen').then((m) => ({ default: m.EnvironmentScreen })));

export const EventsAllScreen = lazyScreen(() =>
  import('./EventsAllScreen').then((m) => ({ default: m.EventsAllScreen })));

export const EventsCalendarScreen = lazyScreen(() =>
  import('./EventsCalendarScreen').then((m) => ({ default: m.EventsCalendarScreen })));

export const EventsFiltersScreen = lazyScreen(() =>
  import('./EventsFiltersScreen').then((m) => ({ default: m.EventsFiltersScreen })));

export const FeedScreen = lazyScreen(() =>
  import('./FeedScreen').then((m) => ({ default: m.FeedScreen })));

export const ForgotPasswordScreen = lazyScreen(() =>
  import('./ForgotPasswordScreen').then((m) => ({ default: m.ForgotPasswordScreen })));

export const FundDistributionScreen = lazyScreen(() =>
  import('./FundDistributionScreen').then((m) => ({ default: m.FundDistributionScreen })));

export const FundOnboardingScreen = lazyScreen(() =>
  import('./FundOnboardingScreen').then((m) => ({ default: m.FundOnboardingScreen })));

export const FundScreen = lazyScreen(() =>
  import('./FundScreen').then((m) => ({ default: m.FundScreen })));

export const FundTransparencyScreen = lazyScreen(() =>
  import('./FundTransparencyScreen').then((m) => ({ default: m.FundTransparencyScreen })));

export const GamificationPreviewScreen = lazyScreen(() =>
  import('./dashboard/user/lightPathChunk').then((m) => ({ default: m.GamificationPreviewScreen })));

export const GuestRoomScreen = lazyScreen(() =>
  import('./GuestRoomScreen').then((m) => ({ default: m.GuestRoomScreen })));

export const HowItWorksScreen = lazyScreen(() =>
  import('./HowItWorksScreen').then((m) => ({ default: m.HowItWorksScreen })));

export const NewsScreen = lazyScreen(() =>
  import('./news/NewsScreen').then((m) => ({ default: m.NewsScreen })));

export const NewsArticleScreen = lazyScreen(() =>
  import('./news/NewsScreen').then((m) => ({ default: m.NewsArticleScreen })));

export const LegalDocScreen = lazyScreen(() =>
  import('./LegalDocScreen').then((m) => ({ default: m.LegalDocScreen })));

export const LiveBroadcastScreen = lazyScreen(() =>
  import('./LiveBroadcastScreen').then((m) => ({ default: m.LiveBroadcastScreen })));

export const LiveDetailScreen = lazyScreen(() =>
  import('./LiveDetailScreen').then((m) => ({ default: m.LiveDetailScreen })));

export const LiveRoomScreen = lazyScreen(() =>
  import('./LiveRoomScreen').then((m) => ({ default: m.LiveRoomScreen })));

export const LiveSessionEntryScreen = lazyScreen(() =>
  import('./live-session/entry/LiveSessionEntryScreen').then((m) => ({ default: m.LiveSessionEntryScreen })));

export const LivesScreen = lazyScreen(() =>
  import('./LivesScreen').then((m) => ({ default: m.LivesScreen })));

export const MasterDetailScreen = lazyScreen(() =>
  import('./MasterDetailScreen').then((m) => ({ default: m.MasterDetailScreen })));

export const MasterSubscriptionsScreen = lazyScreen(() =>
  import('./MasterSubscriptionsScreen').then((m) => ({ default: m.MasterSubscriptionsScreen })));

export const MasterWalletScreen = lazyScreen(() =>
  import('./MasterWalletScreen').then((m) => ({ default: m.MasterWalletScreen })));

export const ProgramPurchaseScreen = lazyScreen(() =>
  import('./programs/ProgramPurchaseScreen').then((m) => ({ default: m.ProgramPurchaseScreen })));
// CORE-1534 — оплата подписки Agapēlla.
export const AgapellaSubscriptionScreen = lazyScreen(() =>
  import('./subscription/AgapellaSubscriptionScreen').then((m) => ({ default: m.AgapellaSubscriptionScreen })));

export const ProgramPageScreen = lazyScreen(() =>
  import('./programs/ProgramPageScreen').then((m) => ({ default: m.ProgramPageScreen })));

export const MastersScreen = lazyScreen(() =>
  import('./MastersScreen').then((m) => ({ default: m.MastersScreen })));

export const MemoryFactsScreen = lazyScreen(() =>
  import('./MemoryFactsScreen').then((m) => ({ default: m.MemoryFactsScreen })));

export const MyCalendarScreen = lazyScreen(() =>
  import('./MyCalendarScreen').then((m) => ({ default: m.MyCalendarScreen })));

export const MyCollectionScreen = lazyScreen(() =>
  import('./MyCollectionScreen').then((m) => ({ default: m.MyCollectionScreen })));

export const MyLibraryScreen = lazyScreen(() =>
  import('./MyLibraryScreen').then((m) => ({ default: m.MyLibraryScreen })));

export const NotificationSettingsScreen = lazyScreen(() =>
  import('./NotificationSettingsScreen').then((m) => ({ default: m.NotificationSettingsScreen })));

export const PracticeDetailScreen = lazyScreen(() =>
  import('./PracticeDetailScreen').then((m) => ({ default: m.PracticeDetailScreen })));

export const PracticeRoomsScreen = lazyScreen(() =>
  import('./PracticeRoomsScreen').then((m) => ({ default: m.PracticeRoomsScreen })));

export const PracticesScreen = lazyScreen(() =>
  import('./PracticesScreen').then((m) => ({ default: m.PracticesScreen })));

export const PublicUserProfileScreen = lazyScreen(() =>
  import('./PublicUserProfileScreen').then((m) => ({ default: m.PublicUserProfileScreen })));

export const FinanceDocumentsScreen = lazyScreen(() =>
  import('./FinanceDocumentsScreen').then((m) => ({ default: m.FinanceDocumentsScreen })));
export const PayoutDetailsScreen = lazyScreen(() =>
  import('./PayoutDetailsScreen').then((m) => ({ default: m.PayoutDetailsScreen })));

export const ReferralCreditsScreen = lazyScreen(() =>
  import('./ReferralCreditsScreen').then((m) => ({ default: m.ReferralCreditsScreen })));

export const RegisterScreen = lazyScreen(() =>
  import('./RegisterScreen').then((m) => ({ default: m.RegisterScreen })));

export const ResetPasswordScreen = lazyScreen(() =>
  import('./ResetPasswordScreen').then((m) => ({ default: m.ResetPasswordScreen })));

export const RoomAnnouncementScreen = lazyScreen(() =>
  import('./RoomAnnouncementScreen').then((m) => ({ default: m.RoomAnnouncementScreen })));

export const SanghaScreen = lazyScreen(() =>
  import('./SanghaScreen').then((m) => ({ default: m.SanghaScreen })));

export const SecuritySettingsScreen = lazyScreen(() =>
  import('./SecuritySettingsScreen').then((m) => ({ default: m.SecuritySettingsScreen })));

export const SettingsScreen = lazyScreen(() =>
  import('./SettingsScreen').then((m) => ({ default: m.SettingsScreen })));

export const SoundLibraryScreen = lazyScreen(() => import('./SoundLibraryScreen'));

export const SupportScreen = lazyScreen(() =>
  import('./InfoPageScreen').then((m) => ({ default: m.SupportScreen })));

export const UploadPracticeScreen = lazyScreen(() =>
  import('./UploadPracticeScreen').then((m) => ({ default: m.UploadPracticeScreen })));


/** SanghaScreen is registered as «Community» in the navigator. */
export const CommunityScreen = SanghaScreen;

/**
 * CORE-1299 — чанки экранов, на которые `LoginScreen` возвращает вошедшего
 * (`navigateInternalReturnTo`), по имени маршрута. `LiveDetail` рисует
 * `LiveDetailRoute`, а он — ленивый `LiveDetailScreen`.
 */
const RETURN_SCREEN_LOADERS: Record<string, () => Promise<unknown>> = {
  Dashboard: DashboardScreen.preload,
  LiveDetail: LiveDetailScreen.preload,
  LiveRoomSession: LiveRoomScreen.preload,
  LiveSessionEntry: LiveSessionEntryScreen.preload,
  RoomAnnouncement: RoomAnnouncementScreen.preload,
  Lives: LivesScreen.preload,
  Masters: MastersScreen.preload,
  Practices: PracticesScreen.preload,
  ProgramPage: ProgramPageScreen.preload,
  ProgramPurchase: ProgramPurchaseScreen.preload,
  AgapellaSubscription: AgapellaSubscriptionScreen.preload,
};

/**
 * Скачать чанк экрана маршрута, если он ленивый. Никогда не отклоняется:
 * сбой загрузки чанка — дело Suspense и его границы ошибок, а не вызывающего.
 */
export function preloadScreen(routeName: string): Promise<void> {
  const load = RETURN_SCREEN_LOADERS[routeName];
  return load ? load().then(() => undefined, () => undefined) : Promise.resolve();
}
